import {
  Editor,
  Element,
  type Node,
  Path,
  type Point,
  Range,
  Text,
  Transforms,
} from 'slate';

export interface Image extends Element {
  type: 'image';
  href?: string; // href the image links to (if linked image)
  url?: string; // image src
  file?: File;
  hasCaption?: boolean;
  width?: number;
}

export const isImage = (node: Node): node is Image => node.type === 'image';

export const withImages = <T extends Editor>(editor: T) => {
  const {
    isVoid,
    insertData,
    insertBreak,
    deleteBackward,
    normalizeNode,
    getFragment,
  } = editor;

  editor.isVoid = element => {
    return isImage(element) ? !element.hasCaption : isVoid(element);
  };

  // https://github.com/ianstormtaylor/slate/issues/4542
  // a selection fully inside one image produces a fragment wrapped in the
  // image element. flatten to inline so pasted content inserts only
  // the caption text, not the wrapping image.
  editor.getFragment = () => {
    const {selection} = editor;

    if (selection != null) {
      const [start, end] = Range.edges(selection);
      const startImage = Editor.above<Image>(editor, {
        at: start.path,
        match: n => isImage(n),
      });
      const endImage = Editor.above<Image>(editor, {
        at: end.path,
        match: n => isImage(n),
      });
      // if ancestor images are the same, meaning we selected the entire caption,
      // flatten to inline so pasted content inserts only.
      if (
        startImage != null &&
        endImage != null &&
        startImage[0]?.hasCaption &&
        Path.equals(startImage[1], endImage[1])
      ) {
        return flattenToInline(editor, getFragment());
      }
    }

    return getFragment();
  };

  editor.insertData = data => {
    for (let i = 0; i < data.files.length; i++) {
      const file = data.files[i];
      if (file.type.includes('image')) {
        EditorWithImages.insertImageFromData(editor, file);
        return;
      }
    }

    insertData(data);
  };

  editor.insertBreak = () => {
    const imageEntry = Editor.above<Image>(editor, {match: n => isImage(n)});
    if (imageEntry != null && imageEntry[0].hasCaption) {
      Transforms.splitNodes(editor, {always: true});
      Transforms.setNodes(editor, {type: 'paragraph'});
      return;
    }

    insertBreak();
  };

  editor.deleteBackward = (...args) => {
    const imageEntry = Editor.above(editor, {match: n => isImage(n)});

    if (imageEntry != null && Editor.isEmpty(editor, imageEntry[0])) {
      Transforms.unsetNodes(editor, ['hasCaption'], {at: imageEntry[1]});

      // hacky selection reset because setting voids breaks Slate's selection synchronization
      Transforms.deselect(editor);
      window.setTimeout(() => {
        Transforms.select(editor, imageEntry[1]);
      });
      return;
    }

    deleteBackward(...args);
  };

  editor.normalizeNode = entry => {
    const [node, path] = entry;

    if (isImage(node)) {
      if (node.file != null && !(node.file instanceof Blob)) {
        // Some situations (like an incomplete upload) can cause the draft spec
        // to be saved with the file property still set. JSON can't serialize it,
        // so it gets saved as a plain object. On reload, this object is treated
        // as a File, which can cause a crash.
        // See https://wandb.atlassian.net/browse/WB-5750
        Transforms.unsetNodes(editor, 'file', {at: path});
        return;
      }
    }

    normalizeNode(entry);
  };

  return editor;
};

interface EditorWithImagesInterface {
  insertImageFromData(
    editor: Editor,
    file: File,
    options?: {
      at?: Point | Range | Path | undefined;
      clone?: boolean;
    }
  ): void;
}

/**
 * recursively flatten to an array of text or inline nodes removing
 * all block nodes.
 */
function flattenToInline(editor: Editor, nodes: Node[]): Node[] {
  const result: Node[] = [];
  for (const node of nodes) {
    if (Text.isText(node)) {
      result.push(node);
    } else if (Element.isElement(node)) {
      if (editor.isInline(node)) {
        result.push(node);
      } else {
        result.push(...flattenToInline(editor, node.children));
      }
    }
  }
  return result;
}

export const EditorWithImages: EditorWithImagesInterface = {
  insertImageFromData(editor, file, options = {}) {
    const {clone = true, ...insertOptions} = options;
    Transforms.insertNodes(
      editor,
      {
        type: 'image',
        children: [{text: ''}],
        file: clone ? new File([file], file.name, {type: file.type}) : file,
      },
      insertOptions
    );
  },
};
